/**
 * useBunnyUrl — reactive render-time Bunny URL resolver.
 *
 * Persisted Bunny signed URLs expire, and they flow through caches (companyCache
 * 24h, Pinia) whose TTLs would serve a stale URL if we resolved at load time.
 * So we resolve at RENDER time instead: pass reactive pointers (assetId/path)
 * and a legacy `fallback`, and the returned `url` ref updates as they change
 * and after each async resolve.
 *
 * Usage:
 *   const { url } = useBunnyUrl(() => ({
 *     assetId: company.value?.logoAssetId,
 *     fallback: company.value?.logoUrl,
 *     intent: BUNNY_URL_INTENT.THUMBNAIL,
 *   }))
 */

import { ref, watch, unref } from 'vue';
import { refreshBunnyUrl } from '@/services/bunnyUrlService';
import { BUNNY_URL_INTENT } from '@/utils/bunnyConstants';

function readSource(source) {
  const raw = typeof source === 'function' ? source() : unref(source);
  return raw || {};
}

export function useBunnyUrl(source) {
  const url = ref(null);
  const loading = ref(false);

  async function resolve(force = false) {
    const {
      assetId,
      path,
      intent = BUNNY_URL_INTENT.THUMBNAIL,
      shareToken,
      fallback = null,
      width = null,
    } = readSource(source);

    // Show the fallback immediately ONLY when there's nothing to resolve. When we
    // have an assetId/path, the fallback is often a stale Bunny signed URL that
    // would 403 (a broken-image frame) before the fresh resolve lands — so wait
    // for the resolve instead (the caller's placeholder shows briefly).
    if (!url.value && !unref(assetId) && !unref(path)) url.value = unref(fallback);

    if (!unref(assetId) && !unref(path)) {
      url.value = unref(fallback);
      return;
    }

    loading.value = true;
    const resolved = await refreshBunnyUrl({
      assetId: unref(assetId),
      path: unref(path),
      intent: unref(intent),
      shareToken: unref(shareToken),
      fallback: unref(fallback),
      width: unref(width),
      force,
    });
    url.value = resolved;
    loading.value = false;
  }

  watch(
    () => {
      const s = readSource(source);
      return [unref(s.assetId), unref(s.path), unref(s.intent), unref(s.shareToken), unref(s.width)];
    },
    () => resolve(false),
    { immediate: true }
  );

  // `refresh` forces a re-mint (used by an <img> @error handler when a token
  // expired between resolve and render).
  return { url, loading, refresh: () => resolve(true) };
}
